import { forwardRef, useEffect, useState } from 'react';
import { useApi } from '@/lib/legacy';
import { notifyError, notifySuccess } from '@/lib/notify';
import { uniqueId } from '../../ui/utils';
import { usePolicyPageContext as usePolicyPageContextJs } from './context/PolicyPageContext';
import { ActionButton, ActionRow, ActionSpacer, Grid, IncludeInFormsCheckbox, type Row } from './tabParts';

type NamedInsuredRow = { id: string; name: string };
type PolicyPageState = {
  namedInsureds: NamedInsuredRow[];
  setNamedInsureds: (update: NamedInsuredRow[] | ((prev: NamedInsuredRow[]) => NamedInsuredRow[])) => void;
  endorsements?: Row[];
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: pages/PolicyPage/context/PolicyPageContext.jsx
const usePolicyPageContext = usePolicyPageContextJs as any as () => PolicyPageState;

const columns = [{ headerName: 'Name', field: 'name', editable: true, headerCheckboxSelection: true, checkboxSelection: true }];

/** The policy's named insureds: add, edit, save, include in the forms, download the form. */
export const NamedInsured = forwardRef<
  unknown,
  { policy: Row; setGridReference: (ref: unknown) => void; onSelectionChanged: (context: unknown) => void }
>(({ policy, setGridReference, onSelectionChanged }) => {
  const { updateNamedInsured, downloadEndorsement, removeEndorsementForm } = useApi();
  const { namedInsureds, setNamedInsureds, endorsements = [] } = usePolicyPageContext();
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [include, setInclude] = useState(false);
  const [initialHadEndorsement, setInitialHadEndorsement] = useState(false);

  useEffect(() => {
    if (endorsements.length) {
      const namedInsured = endorsements?.find(({ endorsementType }) => endorsementType === 'namedInsured');
      if (namedInsured?.ordinal) {
        setInclude(true);
        setInitialHadEndorsement(true);
      }
    }
  }, [endorsements]);

  useEffect(() => {
    setNamedInsureds(policy.namedInsureds?.map((insured: string) => ({ id: uniqueId(), name: insured })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policy.namedInsureds]);

  const handleAddRow = () => {
    const newRow = { id: uniqueId(), name: '' };
    setNamedInsureds((prevData) => (Array.isArray(prevData) ? [...prevData, newRow] : [newRow]));
  };

  const handleUpdateNamedInsured = async () => {
    setIsLoading(true);
    try {
      await updateNamedInsured(
        policy.id,
        namedInsureds.filter(({ name }) => name && name.trim() !== '').map(({ name }) => name),
        include && !initialHadEndorsement
      );
      if (initialHadEndorsement && !include) {
        await removeEndorsementForm(policy.id, { endorsementType: 'namedInsured' });
        notifySuccess('Named Insured updated and form removed successfully.');
      } else {
        notifySuccess('Named Insured updated successfully.');
      }
    } catch (error) {
      console.error('Error updating named insureds:', error);
      notifyError('An error occurred while updating named insureds. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadInsured = async () => {
    setIsDownloading(true);
    try {
      const response = await downloadEndorsement(policy.id, { endorsementType: 'namedInsured' });
      if (response) {
        window.open(response.data.value, '_blank');
      }
    } catch (error) {
      console.error('Error saving payload:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div>
      <div className="grid-container max-h-[600px] overflow-y-auto">
        <Grid
          data={namedInsureds}
          columns={columns}
          setGridReference={setGridReference}
          rowSelection="multiple"
          shouldShowPagination={false}
          columnWidth={150}
          floatingFilter={false}
          onSelectionChanged={onSelectionChanged}
        />
      </div>
      <ActionRow>
        <ActionButton onClick={handleAddRow}>Add Insured</ActionButton>
        <ActionButton onClick={handleUpdateNamedInsured} loading={isLoading}>
          Save
        </ActionButton>
        <ActionButton onClick={handleDownloadInsured} loading={isDownloading}>
          Download
        </ActionButton>
        <ActionSpacer />
        <IncludeInFormsCheckbox checked={include} onChange={setInclude} />
        <ActionSpacer />
      </ActionRow>
    </div>
  );
});
NamedInsured.displayName = 'NamedInsured';
